/**
 * Thin fetch wrapper.
 *
 * - Prefixes relative paths with VITE_API_BASE.
 * - Injects the JWT `Authorization: Bearer` header when a token is present.
 * - On 401 it clears auth and hard-redirects to /login (session expired).
 * - Parses JSON and surfaces a typed HttpError on non-2xx.
 */

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '';

const TOKEN_KEY = 'rt.auth.token';

/** Read the persisted token without importing the auth store (avoids a cycle). */
function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearTokenAndRedirect(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore storage errors */
  }
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

export class HttpError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable body; set automatically with the right Content-Type. */
  json?: unknown;
  /** Skip Authorization injection (e.g. login endpoints). */
  anonymous?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, anonymous, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (!anonymous) {
    const token = readToken();
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const response = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : null,
  });

  if (response.status === 401 && !anonymous) {
    clearTokenAndRedirect();
    throw new HttpError(401, 'Unauthorized', null);
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const parsed: unknown = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const message =
      (isJson &&
        typeof parsed === 'object' &&
        parsed !== null &&
        'message' in parsed &&
        typeof (parsed as { message: unknown }).message === 'string' &&
        (parsed as { message: string }).message) ||
      `HTTP ${response.status}`;
    throw new HttpError(response.status, message, parsed);
  }

  return parsed as T;
}

export const http = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'POST' }),
  patch: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH' }),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
